import { Box, Button, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ResetPasswordFormSchema } from '../../schema';

import { ResetSuccess } from './components/ResetSuccess';

import { Colors, Key } from '@/assets';
import { Alert, InputText, Loading, PrimaryButton, showToatify } from '@/components';
import { APIresetPassword, ResetPasswordForm } from '@/features/auth';
import { classValidatorFormResolverFactory } from '@/helper';
import { useMessageData } from '@/hooks';
import { Screens } from '@/routes';

const ResetPasswordFormSchemaValidatorResolver = classValidatorFormResolverFactory<ResetPasswordFormSchema>(ResetPasswordFormSchema);

export const PasswordReset = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const navigate = useNavigate();
    const { isSuccess, message, setMessage, clearMessage } = useMessageData();

    const [loading, setLoading] = useState<boolean>(false);
    const [newPassword, setNewPassword] = useState<string | undefined>();
    const form = useForm<ResetPasswordFormSchema>({
        resolver: ResetPasswordFormSchemaValidatorResolver
    });

    const onSubmit = async (data: ResetPasswordForm) => {
        try {
            if (!token) return;
            const { password } = data;
            setLoading(true);
            await APIresetPassword(password, token);
            showToatify('Password reset successfully');
            setNewPassword(password);
        } catch (error: any) {
            setMessage({ message: error.message });
        } finally {
            setLoading(false);
        }
    };

    if (newPassword) {
        return <ResetSuccess newPassword={newPassword} />;
    }

    return (
        <FormProvider {...form}>
            <Loading loading={loading} />
            <Key />
            <Typography variant="h1" sx={{ mt: 3, fontWeight: 600, color: Colors.tangaroaBlue }}>
                Set new password
            </Typography>
            <Typography variant="h6" sx={{ mt: 2, fontWeight: 'normal', color: Colors.lynchBlue, textAlign: 'center' }}>
                Your new password must be different to previously used passwords.
            </Typography>
            <form style={{ width: '100%' }} onSubmit={form.handleSubmit(onSubmit)}>
                <Box sx={{ mt: 3 }}>
                    <Alert isSuccess={isSuccess} message={message} clearMessage={clearMessage} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <InputText type="password" name="password" label="Password" placeholder="••••••••" />
                        <InputText type="password" name="confirmPassword" label="Confirm password" placeholder="••••••••" />
                        <PrimaryButton title="Reset password" submit />
                        <Button onClick={() => navigate(Screens.Login)} sx={{ color: Colors.lynchBlue, py: 1.5, boxShadow: 'none' }}>
                            <i className="far fa-arrow-left"></i>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500, color: Colors.lynchBlue, ml: 0.5 }}>
                                Back to log in
                            </Typography>
                        </Button>
                    </Box>
                </Box>
            </form>
        </FormProvider>
    );
};
